import { axios } from "../config/axios";

const endPoint = '/project/venue-master';

// The API names location ids after their tables (cityMasterId ...); the Venue Management
// screen works with short names (cityId ...). These map between the two.
const PARENT_FIELD = { state: 'countryMasterId', city: 'stateMasterId' };

const toVenue = (venue) => ({
    ...venue,
    cityId: venue.cityMasterId,
    stateId: venue.stateMasterId,
    countryId: venue.countryMasterId,
});

const toVenueBody = ({ cityId, ...venue }) => ({ ...venue, cityMasterId: cityId });

const mapData = (res, mapper) => ({ ...res, data: mapper(res.data) });

// Countries, states and cities with their child counts, for the Locations tiles and pickers.
const getLocations = async () => {
    const res = await axios.get(`${endPoint}/locations`);
    return mapData(res, (data) => ({
        countries: data.countries.map((country) => ({ id: country.id, name: country.countryName, stateCount: country.stateCount })),
        states: data.states.map((state) => ({ id: state.id, countryId: state.countryMasterId, name: state.stateName, cityCount: state.cityCount })),
        cities: data.cities.map((city) => ({ id: city.id, stateId: city.stateMasterId, name: city.cityName, venueCount: city.venueCount })),
    }));
}

// type: 'country' | 'state' | 'city'; body: { name, countryId?, stateId? }
const createLocation = (type, body) => {
    const parentField = PARENT_FIELD[type];
    const parentId = type === 'state' ? body.countryId : body.stateId;
    return axios.post(`${endPoint}/locations/${type}`, { name: body.name, ...(parentField ? { [parentField]: parentId } : {}) });
}

const deleteLocation = (type, id) => {
    return axios.delete(`${endPoint}/locations/${type}/${id}`);
}

// Count and totalCapacity cover every venue matching search/state, not only the current page.
const getVenueListByPage = async ({ search, stateId, page, limit }) => {
    const res = await axios.post(`${endPoint}/pagination`, { search, stateMasterId: stateId || null, page, limit });
    return mapData(res, (data) => ({ ...data, rows: data.rows.map(toVenue) }));
}

// Venues with the same name (ignoring case), for the duplicate-name warning.
const findVenuesByName = async (venueName, excludeId = null) => {
    const res = await axios.get(`${endPoint}/same-name`, { params: { venueName, excludeId } });
    return mapData(res, (data) => data.map(toVenue));
}

const createVenue = async (body) => {
    const res = await axios.post(`${endPoint}`, toVenueBody(body));
    return mapData(res, toVenue);
}

const updateVenueById = async (venueId, body) => {
    const res = await axios.put(`${endPoint}/${venueId}`, toVenueBody(body));
    return mapData(res, toVenue);
}

// Venues are never deleted; a disabled venue stays listed but cannot be used by new candidate uploads.
const changeVenueActiveStatus = (venueId, body) => {
    return axios.put(`${endPoint}/change-active-status/${venueId}`, body);
}

// Response: { added: { countries, states, cities, venues }, updated, skipped, errors: [{ row, message }] }
const uploadVenueFile = (type, file) => {
    const body = new FormData();
    body.append('type', type);
    body.append('file', file);
    return axios.post(`${endPoint}/bulk-upload`, body);
}

// Imports venues from the exam venue details service (the backend holds its URL and key).
// Same response as uploadVenueFile; error rows name the venue code.
const importVenues = () => {
    return axios.post(`${endPoint}/import`);
}

const downloadVenueTemplate = (type) => {
    return axios.get(`${endPoint}/template/${type}`, {
        responseType: 'blob',
    });
}

export {
    getLocations,
    createLocation,
    deleteLocation,
    getVenueListByPage,
    findVenuesByName,
    createVenue,
    updateVenueById,
    changeVenueActiveStatus,
    uploadVenueFile,
    importVenues,
    downloadVenueTemplate,
}
